// Currency display helpers.
//
// All billing surfaces in the EMR were hard-coded to USD, which puts a `$`
// in front of every amount even for clinics in Egypt or the GCC. Until we
// wire per-tenant currency through the backend, the display currency is a
// frontend-only preference: detected from the browser locale on first use,
// overridable from System Settings → Currency display, persisted in
// localStorage.

const STORAGE_KEY = 'maie.billing.displayCurrency';

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  EG: 'EGP',
  SA: 'SAR',
  AE: 'AED',
  QA: 'QAR',
  KW: 'KWD',
  BH: 'BHD',
  OM: 'OMR',
  JO: 'JOD',
  LB: 'LBP',
  IQ: 'IQD',
  GB: 'GBP',
  EU: 'EUR',
  DE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  US: 'USD',
};

const SUPPORTED_CODES = [
  'USD', 'EUR', 'GBP',
  'EGP', 'SAR', 'AED', 'QAR', 'KWD', 'BHD', 'OMR',
  'JOD', 'LBP', 'IQD',
];

const detectFromLocale = (): string => {
  try {
    const lang =
      (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
    const region = lang.split(/[-_]/)[1]?.toUpperCase();
    if (region && COUNTRY_TO_CURRENCY[region]) {
      return COUNTRY_TO_CURRENCY[region];
    }
    return 'USD';
  } catch {
    return 'USD';
  }
};

export const isSupportedCurrencyCode = (code: string): boolean =>
  SUPPORTED_CODES.includes(code.toUpperCase());

export const SUPPORTED_CURRENCY_CODES: readonly string[] = SUPPORTED_CODES;

export const getDisplayCurrency = (): string => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isSupportedCurrencyCode(stored)) return stored.toUpperCase();
  } catch {
    // localStorage unavailable (SSR, private mode) — fall through.
  }
  return detectFromLocale();
};

export const setDisplayCurrency = (code: string): void => {
  if (!isSupportedCurrencyCode(code)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, code.toUpperCase());
    window.dispatchEvent(new Event('maie:currency-changed'));
  } catch {
    // ignore — best-effort persistence
  }
};

/**
 * Seed the display currency from a server-side, per-tenant value (the
 * facility's saved `billingCurrency`). This is what makes the choice
 * durable: localStorage is only a per-browser cache, so without a
 * server seed the currency silently reverts to the browser-locale guess
 * (en-GB → GBP) every time that cache is cold — a fresh device, a
 * cleared profile, or a new browser. Call this whenever the tenant's
 * facility config loads. No-ops on empty/unsupported codes or when the
 * cache already matches (so it doesn't spam the change event).
 */
export const seedDisplayCurrencyFromTenant = (
  code: string | null | undefined,
): void => {
  if (!code || !isSupportedCurrencyCode(code)) return;
  const normalized = code.toUpperCase();
  try {
    if (window.localStorage.getItem(STORAGE_KEY) === normalized) return;
    window.localStorage.setItem(STORAGE_KEY, normalized);
    window.dispatchEvent(new Event('maie:currency-changed'));
  } catch {
    // ignore — best-effort persistence
  }
};

export interface FormatCurrencyOptions {
  /** Override the resolved currency code. */
  currency?: string;
  /** BCP-47 locale. Defaults to navigator.language. */
  locale?: string;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
}

export const formatCurrency = (
  amount: number,
  options: FormatCurrencyOptions = {},
): string => {
  const value = Number.isFinite(amount) ? amount : 0;
  const currency = (options.currency ?? getDisplayCurrency()).toUpperCase();
  const locale =
    options.locale ??
    (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(options.maximumFractionDigits ?? 2)}`;
  }
};
